import { useEffect } from 'react'

import { currentPage, currentPageParams, navigateTo } from 'scrivito'

import { TimeTrackingTab } from '../const'
import { FilterValues, WeekRange } from '../types'
import {
  FILTER_PARAM_KEYS,
  FilterParams,
  filtersToParams,
  readFiltersFromParams,
} from './filterParams'
import { resolveUrlDefaults } from './urlDefaults'
import { getCurrentWeekRange, getMonthRange } from '../utils/dateUtils'
import { ensureString } from '@/utils/ensureString'

const isValidTab = (value: string): value is TimeTrackingTab =>
  Object.values(TimeTrackingTab).includes(value as TimeTrackingTab)

const parseWeekRange = (
  start: string,
  end: string,
  fallback: WeekRange,
): WeekRange => (start && end ? { startDate: start, endDate: end } : fallback)

const pickFilterParams = (
  params: Record<string, unknown>,
): Partial<FilterParams> =>
  FILTER_PARAM_KEYS.reduce<Partial<FilterParams>>((acc, key) => {
    const v = ensureString(params[key])
    if (v) acc[key] = v
    return acc
  }, {})

const emptyFilterParams = (): FilterParams =>
  FILTER_PARAM_KEYS.reduce<FilterParams>(
    (acc, k) => ({ ...acc, [k]: '' }),
    {} as FilterParams,
  )

export const useTimeTrackingUrlState = ({
  currentUserId,
  isTeamLeader,
}: {
  currentUserId: string
  isTeamLeader: boolean
}) => {
  const params = currentPageParams()

  const tabFromUrl = ensureString(params.tab)
  const currentTab = isValidTab(tabFromUrl)
    ? tabFromUrl
    : TimeTrackingTab.MY_TIMES

  const defaultWeekRange = getCurrentWeekRange()
  const defaultMonthRange = getMonthRange(new Date())

  const defaults = resolveUrlDefaults({
    params,
    currentTab,
    currentUserId,
    isTeamLeader,
    weekRange: defaultWeekRange,
    monthRange: defaultMonthRange,
  })
  const defaultsKey = new URLSearchParams(defaults).toString()

  useEffect(() => {
    if (!defaultsKey) return
    const next = Object.fromEntries(new URLSearchParams(defaultsKey))
    navigateTo(currentPage(), { params: { ...currentPageParams(), ...next } })
  }, [defaultsKey])

  const myTimesRange = parseWeekRange(
    ensureString(params.start),
    ensureString(params.end),
    defaultWeekRange,
  )
  const allTimesRange = parseWeekRange(
    ensureString(params.allStart),
    ensureString(params.allEnd),
    defaultWeekRange,
  )
  const teamMonthRange = parseWeekRange(
    ensureString(params.teamStart),
    ensureString(params.teamEnd),
    defaultMonthRange,
  )
  const ordersMonthRange = parseWeekRange(
    ensureString(params.ordersStart),
    ensureString(params.ordersEnd),
    defaultMonthRange,
  )

  const filters = readFiltersFromParams(pickFilterParams(params))

  const updateUrl = (newParams: Record<string, string>) => {
    navigateTo(currentPage(), { params: { ...params, ...newParams } })
  }

  const setCurrentTab = (tab: string, extraParams?: Record<string, string>) => {
    updateUrl({ tab, ...emptyFilterParams(), ...(extraParams || {}) })
  }

  const navigateToTab = (
    tab: TimeTrackingTab,
    nextFilters?: Partial<FilterValues>,
  ) => {
    const merged: FilterValues = {
      status: null,
      team: null,
      customer: null,
      order: null,
      workPackage: null,
      ...nextFilters,
    }
    const extras: Record<string, string> = { ...filtersToParams(merged) }

    if (tab === TimeTrackingTab.ALL_TIMES && nextFilters?.dateRange) {
      extras.allStart = nextFilters.dateRange.startDate
      extras.allEnd = nextFilters.dateRange.endDate
    }

    updateUrl({ tab, ...extras })
  }

  const setMyTimesRange = (range: WeekRange) => {
    updateUrl({ start: range.startDate, end: range.endDate })
  }
  const setAllTimesRange = (range: WeekRange) => {
    updateUrl({
      allStart: range.startDate,
      allEnd: range.endDate,
      filterByDate: '',
      dateStart: '',
      dateEnd: '',
    })
  }
  const setTeamMonthRange = (range: WeekRange) => {
    updateUrl({ teamStart: range.startDate, teamEnd: range.endDate })
  }
  const setOrdersMonthRange = (range: WeekRange) => {
    updateUrl({ ordersStart: range.startDate, ordersEnd: range.endDate })
  }

  const setFilters = (next: FilterValues) => {
    updateUrl(filtersToParams(next))
  }

  const clearFilters = () => {
    updateUrl(emptyFilterParams())
  }

  return {
    currentTab,
    myTimesRange,
    allTimesRange,
    teamMonthRange,
    ordersMonthRange,
    filters,
    setCurrentTab,
    navigateToTab,
    setMyTimesRange,
    setAllTimesRange,
    setTeamMonthRange,
    setOrdersMonthRange,
    setFilters,
    clearFilters,
  }
}
